<template>
    <div class="navigation">
        <div class="scenario__name__header" v-if="!editName">
            <h3>{{ $t(scenarioName) }}</h3>
            <i
                v-if="isSimulationPath() && activeScenario !== undefined && activeScenario.id > 0"
                class="mdi mdi-pencil"
                @click="changeName"
            ></i>
        </div>

        <div class="scenario__name__header" v-if="editName">
            <input
                v-model="scenarioName"
                type="text"
                name="name"
                id="name"
                class="input_text small"
                v-on:keyup.enter="updateName"
            />
            <i v-if="!savingName" class="mdi mdi-check icon" @click="updateName"></i>
            <div v-if="savingName" class="icon">
                <div class="spinner"></div>
            </div>
        </div>

        <div class="navigation__link__wrapper">
            <router-link
                id="simtool_new_scenario"
                to="/"
                class="navigation__link"
                v-on:click="newScenario"
            >
                <i class="mdi mdi-plus-circle"></i>
                {{ $t('navigation.newScenario') }}
            </router-link>
        </div>

        <div class="navigation__link__wrapper">
            <a
                id="simtool_save_scenario"
                class="navigation__link"
                v-on:click="saveScenario"
                v-bind:class="{ disabled: !isSimulationPath() }"
            >
                <i class="mdi mdi-cloud-upload"></i>
                {{ $t('navigation.saveScenario') }}
            </a>
        </div>
        <div class="navigation__link__wrapper">
            <a
                id="simtool_export_pdf"
                class="navigation__link"
                v-show="!downloadingPdf"
                v-bind:class="{ disabled: !isSimulationPath() || !simulated }"
                @click="requestPdf()"
            >
                <i class="mdi mdi-file-pdf"></i>
                {{ $t('navigation.export') }}
            </a>
            <div v-show="downloadingPdf">
                <div class="spinner"></div>
            </div>
        </div>

        <div class="navigation__link__wrapper">
            <a id="simtool_share" class="navigation__link" @click="share">
                <i class="mdi mdi-share-variant"></i>
                {{ $t('navigation.share') }}
            </a>
        </div>

        <div class="navigation__link__wrapper">
            <a id="simtool_get_help" class="navigation__link" @click="help">
                <i class="mdi mdi-help-circle"></i>
                {{ $t('navigation.getHelp') }}
            </a>
        </div>

        <div class="navigation__link__wrapper">
            <a
                id="simtool_manual"
                class="navigation__link"
                :href="$t('navigation.manual_url')"
                target="_blank"
            >
                <i class="mdi mdi-book-open-variant"></i>
                {{ $t('navigation.manual') }}
            </a>
        </div>

        <button class="button--primary" v-on:click="quote">{{ $t('navigation.getQuote') }}</button>

        <divider></divider>

        <h3>{{ $t('navigation.units') }}</h3>

        <div class="select">
            <select v-model="active_unit">
                <option
                    v-for="unit in filtered_units"
                    :key="unit.name"
                    :value="unit.name.toLowerCase()"
                >
                    {{ unit.name }}
                </option>
            </select>
        </div>

        <div v-if="scenarios !== undefined && scenarios.length > 0">
            <divider></divider>

            <div class="scenario__name__header" v-if="!searching">
                <h3>{{ $t('navigation.savedScenarios') }}</h3>
                <i class="mdi mdi-magnify" @click="showSearch"></i>
            </div>

            <div class="scenario__name__header" v-if="searching">
                <input
                    v-model="searchTerm"
                    type="text"
                    name="search"
                    id="search"
                    class="input_text small"
                    :placeholder="$t('navigation.filterPlaceholder')"
                />
                <i v-if="!savingName" class="mdi mdi-close icon" @click="stopSearch"></i>
            </div>

            <ul class="navigation__list_icon list--dash">
                <li
                    v-for="scenario in scenarios"
                    :key="scenario.id"
                    v-show="
                        scenario.name !== undefined &&
                            scenario.name.toLowerCase().indexOf(searchTerm.toLowerCase()) > -1
                    "
                >
                    <router-link v-bind:to="'/scenarios/' + scenario.id">
                        {{ scenario.name }}
                    </router-link>
                    <i class="mdi mdi-delete icon--small" @click="removeScenario(scenario)"></i>
                </li>
            </ul>
        </div>

        <divider></divider>

        <button class="button--tertiary" @click="signout">{{ $t('navigation.signOut') }}</button>
        <language-selector />
        <divider></divider>
    </div>
</template>

<script>
import Vue from 'vue';
import Component from 'vue-class-component';
import Divider from './Divider';
import { mapGetters, mapState } from 'vuex';
import { Watch } from 'vue-property-decorator';
import { CHANGE_UNIT, RESET_AUTH } from '../store/types';
import Scenario from '../models/Scenario';
import Motor from '../models/Motor';
import { isTorqueUnit, isSI, isImperial } from '../helpers';
import LanguageSelector from './LanguageSelector.vue';

@Component({
    components: {
        LanguageSelector,
    },
    computed: {
        ...mapGetters({
            scenarios: 'getScenarios',
            activeScenario: 'getActiveScenario',
            activeScenarioId: 'getActiveScenarioId',
            units: 'getUnits',
        }),
        ...mapState({
            state_unit: state => state.unit,
        }),
    },
})
export default class Navigation extends Vue {
    name = 'navigation';

    scenarioName = '';

    downloadingPdf = false;
    requestingQuote = false;
    navigating = false;

    active_unit = this.getUnit();
    filtered_units = [];

    simulated = false;

    editName = false;
    savingName = false;
    originalName = '';

    searching = false;
    searchTerm = '';

    /**
     * Should torque unit profiles be shown?
     * @type {boolean}
     */
    isTorque = false;

    saveScenario() {
        if (this.isSimulationPath()) {
            document.dispatchEvent(new CustomEvent('save-scenario'));
        }
    }

    newScenario() {
        this.$store.dispatch('newScenario');
    }

    removeScenario(scenario) {
        let data = {
            name: scenario.name,
            id: scenario.id,
        };
        this.$eventHub.$emit('remove-scenario', data);
    }

    @Watch('state_unit')
    selectedUnitChanged() {
        this.active_unit = this.state_unit;
    }

    @Watch('active_unit')
    unitChanged() {
        this.$store.dispatch('changeUnit', this.active_unit);

        var details = {
            detail: {
                unit: this.active_unit,
            },
        };

        document.dispatchEvent(new CustomEvent('unit-changed', details));
    }

    getUnit() {
        let state = this.state_unit;
        let storage = localStorage.getItem('unit');
        let defUnit = 'si';

        let units;

        if (!state && storage) {
            units = storage;

            if (units === undefined || units === null) {
                units = defUnit;
            }
        } else if (state) {
            units = state;

            if (units === undefined || units === null) {
                units = defUnit;
            }
        } else {
            units = defUnit;
        }

        this.$store.dispatch('changeUnit', units);

        var details = {
            detail: {
                unit: units,
            },
        };

        document.dispatchEvent(new CustomEvent('unit-changed', details));

        return units;
    }

    @Watch('units')
    unitsChanged() {
        this.filtered_units = this.filterUnits(this.units, false, this.isTorque);
    }

    beforeMount() {
        this.filtered_units = this.filterUnits(this.units);
    }

    mounted() {
        this.scenarioName = 'navigation.noScenarioSelected';

        //Confirm reload if there are unsaved changes
        //TODO find out why onbeforeunload is not called every time.
        window.onbeforeunload = e => {
            if (this.editName && this.originalName !== this.scenarioName) {
                e.returnValue = 'Test';
                return 'Test';
            }
        };

        this.$eventHub.$on('request-pdf-complete', () => {
            //Pdf is done

            this.downloadingPdf = false;
        });

        this.$eventHub.$on('request-quote-complete', () => {
            this.requestingQuote = false;
        });

        this.$eventHub.$on('scenario-simulated', () => {
            this.simulated = true;
        });

        this.$eventHub.$on('scenario-error', () => {
            this.simulated = false;
        });

        this.$eventHub.$on('scenario-invalid', () => {
            this.simulated = false;
        });

        this.$eventHub.$on('navigated', () => {
            this.stopNameChange();
        });

        this.$eventHub.$on('activate-scenario', data => {
            let scenario = data.scenario;
            let id = data.id;
            this.editName = false;

            let prevTorque = this.isTorque;

            if (scenario === undefined && id < 0) {
                this.scenarioName = 'navigation.noScenarioSelected';
                this.isTorque = false;
            } else if (scenario !== undefined && id < 0 && scenario.name === undefined) {
                this.scenarioName = 'navigation.newScenario';
                this.isTorque = this.isTorqueName(
                    scenario.motor !== undefined ? scenario.motor.name : '',
                );
            } else {
                this.scenarioName = scenario.name;
                this.isTorque = scenario.isTorque();
            }

            this.filtered_units = this.filterUnits(this.units, prevTorque, this.isTorque);
        });

        //When the motor of a scenario changes, check if the units should be changed.
        this.$eventHub.$on('motor-changed', data => {
            let prevTorque = this.isTorque;
            this.isTorque = data.isTorque;

            this.filtered_units = this.filterUnits(this.units, prevTorque, this.isTorque);
        });

        this.$router.beforeEach((to, from, next) => {
            this.navigating = true;
            this.routeLeave(to, from, next);
            this.navigating = false;
        });
    }

    isTorqueName(name) {
        return name !== undefined && name !== null && name.toLowerCase().startsWith('q');
    }

    routeLeave(to, from, next) {
        if (this.editName && this.originalName !== this.scenarioName) {
            console.error('CONFIRM NAVIGATION', '');
            this.$eventHub.$emit('confirm-navigation', {
                next: next,
            });

            return;
        }

        next();
    }

    requestPdf() {
        if (this.isSimulationPath() && this.simulated) {
            this.downloadingPdf = true;
            this.$eventHub.$emit('request-pdf', {
                name: this.scenarioName,
            });
        }
    }

    quote() {
        this.requestingQuote = true;
        this.$eventHub.$emit('request-quote');
    }

    help() {
        this.$eventHub.$emit('request-help');
    }

    share() {
        this.$eventHub.$emit('request-share');
    }

    isSimulationPath() {
        return (
            !this.navigating &&
            (this.$route.path.indexOf('scenario') > -1 || this.$route.path.indexOf('share') > -1)
        );
    }

    signout() {
        this.$store.commit(RESET_AUTH);
        this.$router.push({ name: 'Login' });
    }

    changeName() {
        this.editName = true;
        this.originalName = this.scenarioName;
    }

    updateName() {
        if (this.originalName !== this.scenarioName && this.scenarioName.length > 0) {
            this.savingName = true;

            if (this.activeScenarioId > -1) {
                let scenario = Scenario.fromJson(this.activeScenario);
                scenario.name = this.scenarioName;

                if (this.activeScenario.motor === undefined) {
                    let motor = new Motor();
                    motor.name = 'TB12N';
                    scenario.motor = motor;
                }

                scenario
                    .save()
                    .then(() => {
                        this.savingName = false;
                        this.editName = false;
                        this.originalName = undefined;

                        this.$store.dispatch('updateScenario', scenario.toJson());
                    })
                    .catch(() => {
                        this.savingName = false;
                        this.editName = false;
                        this.originalName = undefined;

                        //TODO show error message.
                    });
            }
        } else {
            this.savingName = false;
            this.editName = false;
            this.scenarioName = this.originalName;
            this.originalName = undefined;
        }
    }

    stopNameChange() {
        this.savingName = false;
        this.editName = false;
        this.scenarioName = this.originalName;
        this.originalName = undefined;
    }

    showSearch() {
        this.searching = true;
    }

    stopSearch() {
        this.searching = false;
        this.searchTerm = '';
    }

    /**
     * Filters the unit profiles based on the motor type (torque or linear)
     * @param units
     * @param prevTorque
     * @returns {*}
     */
    //TODO clean up the unit filtering based on the newly added isTorque field for unit profiles.
    filterUnits(units, prevTorque = false, isTorque = false) {
        let filtered = [];

        let active = undefined;
        if (units !== null && units !== undefined) {
            for (let i = 0; i < units.length; i++) {
                if (
                    (!isTorque || !this.isSimulationPath()) &&
                    !isTorqueUnit(units[i]) &&
                    !units[i].hidden
                ) {
                    filtered.push(units[i]);
                } else if (
                    isTorque &&
                    this.isSimulationPath() &&
                    isTorqueUnit(units[i]) &&
                    !units[i].hidden
                ) {
                    filtered.push(units[i]);
                }

                if (
                    this.active_unit !== undefined &&
                    this.active_unit.toLowerCase() === units[i].name.toLowerCase() &&
                    !units[i].hidden
                ) {
                    active = units[i];
                }
            }
        }

        this.fallback(units, filtered, active, prevTorque);
        //Motor switched from torque to linear. Change selected unit to a fallback.

        return filtered;
    }

    /**
     * Set a unit profile fallback as the active profile
     */
    fallback(allProfiles = [], filteredProfiles = [], from, prevTorque) {
        //If we need to select a torque unit while the previously selected profile was not torque.
        let changeTo;

        if (this.isTorque && !isTorqueUnit(from)) {
            //We switched from a linear scenario to a torque scenario
            let saved = localStorage.getItem('torque_unit');
            if (!saved) {
                if (isImperial(from)) {
                    changeTo = allProfiles.find(unit => {
                        return isTorqueUnit(unit) && isImperial(unit);
                    });

                    if (changeTo === undefined) {
                        changeTo = allProfiles.find(unit => {
                            return isTorqueUnit(unit) && isSI(unit);
                        });
                    }
                } else {
                    changeTo = allProfiles.find(unit => {
                        return isTorqueUnit(unit) && isSI(unit);
                    });
                }
            } else {
                changeTo = allProfiles.find(unit => {
                    return unit.name.toLowerCase() === saved;
                });
            }
        } else if (!this.isTorque && isTorqueUnit(from)) {
            // We switched from a torque scenario to a linear scenario
            let saved = localStorage.getItem('unit');
            if (!saved) {
                if (isImperial(from)) {
                    changeTo = allProfiles.find(unit => {
                        return !isTorqueUnit(unit) && isImperial(unit);
                    });

                    if (changeTo === undefined) {
                        changeTo = allProfiles.find(unit => {
                            return !isTorqueUnit(unit) && isSI(unit);
                        });
                    }
                } else {
                    changeTo = allProfiles.find(unit => {
                        return !isTorqueUnit(unit) && isSI(unit);
                    });
                }
            } else {
                changeTo = allProfiles.find(unit => {
                    return unit.name.toLowerCase() === saved;
                });
            }
        }

        if (changeTo !== undefined) {
            this.active_unit = changeTo.name.toLowerCase();
            this.$store.dispatch('changeUnit', this.active_unit);

            var details = {
                detail: {
                    unit: this.active_unit,
                },
            };

            document.dispatchEvent(new CustomEvent('unit-changed', details));
        }
    }
}

Vue.component('navigation', Navigation);
</script>
